import { useNavigate } from 'react-router-dom';
import { useAuthStore } from 'src/store/auth';
import { useApplicationStore } from 'src/store/application';
import { useGeneralStore } from 'src/store';
import { isW2HostNow } from '@features/w2/host/w2Host';
import type { LoginRequest } from 'src/services/interfaces/auth';

/**
 * Read a validated post-login redirect target from `?next=`. Returns the path
 * only when it resolves to a same-app absolute path on OUR origin, so a crafted
 * `?next=//evil.com`, `?next=https://evil.com`, `?next=/\evil.example` (a
 * backslash the browser folds to `/` in authority position) or
 * `?next=/..//evil.example` (a `..` segment the URL parser normalizes INTO an
 * authority) can never redirect the user off-site.
 *
 * The gate is a single authoritative rule applied TWICE, because the value we
 * return is itself re-resolved by the caller (`window.location.href = target`):
 *
 *   1. resolve the raw candidate against our origin with the WHATWG URL parser —
 *      the same parser the browser uses — and require the resolved origin to be
 *      identical to ours. This rejects `//host`, `/\host`, `https://host` and
 *      every other smuggled-authority form in one check, without enumerating
 *      string shapes;
 *   2. resolve the STRING WE RETURN the same way and require the same equality.
 *      Step 1 alone is not enough: `new URL('/..//evil.example', origin)` stays
 *      on our origin but normalizes to `pathname === '//evil.example'`, which is
 *      scheme-relative once assigned to `location.href` — that re-resolution is
 *      the actual navigation, so it is the one that must be same-origin.
 *
 * Control characters are rejected up front: the URL parser silently STRIPS tab,
 * LF and CR before parsing, so `/<TAB>/evil.example` is a different string to us
 * than to the parser — never reason about a value the parser will rewrite.
 */
export function safeNextTarget(): string | null {
  if (typeof window === 'undefined') return null;
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next) return null;
  // The parser strips these before parsing; refuse rather than guess.
  if (/[\u0000-\u001F\u007F]/.test(next)) return null;
  // Must be an in-app absolute path — a bare `select-plans` would resolve
  // same-origin but is not a target we ever emit.
  if (!next.startsWith('/')) return null;
  const origin = window.location.origin;
  try {
    // (1) the candidate must resolve onto our origin …
    const resolved = new URL(next, origin);
    if (resolved.origin !== origin) return null;
    // … and (2) so must the string we hand back, which the caller re-resolves.
    const target = `${resolved.pathname}${resolved.search}${resolved.hash}`;
    if (new URL(target, origin).origin !== origin) return null;
    return target;
  } catch {
    return null;
  }
}

/**
 * @param onSuccess Overrides the default post-login side effect (the B2C
 * subscription-aware hard nav to `/healthhub` or `/form`). The W2 employee flow
 * passes its enroll step here so the same branded login screen is reused without
 * leaving the token flow.
 */
export const useLogin = (onSuccess?: () => void) => {
  const { login: loginAction, error, isLoading, clear } = useAuthStore();
  const navigate = useNavigate();

  const handleLogin = async (credentials: LoginRequest) => {
    // Capture the `?next=` target BEFORE authenticating: a successful login flips
    // the auth store, and `PublicLayout` immediately SPA-redirects the (now
    // authenticated) `/login` route to `/form` — which clears the query string.
    // Reading `window.location.search` after the await would race that redirect
    // and miss the target (Risk R8), so snapshot it up front.
    const nextTarget = safeNextTarget();

    await loginAction(credentials);

    // Successful authentication should store a token
    if (localStorage.getItem('token')) {
      // W2 employee flow: hand off to the enroll step instead of the B2C nav.
      if (onSuccess) {
        onSuccess();
        return;
      }
      // Post-login redirect target (`?next=`): the W2 self-selection surface
      // sends returning employees to `/login?next=/select-plans` (Risk R8). Honor
      // only same-app absolute paths (validated in `safeNextTarget` — never
      // `//host` or an absolute URL) so this can't be an open redirect. Hard nav
      // so the destination boots cleanly with the freshly stored JWT.
      if (nextTarget) {
        window.location.href = nextTarget;
        return;
      }
      // W2 host: a standalone `/login` (no `?next=`) must land on the W2 stage
      // resolver at `/select-plans` — NEVER the B2C onboarding/profile home,
      // which would let the employee create B2C state on the W2 group. The page
      // resolves PHQ/waiver/selection/signature state from the authenticated
      // selection context before it renders any editable selection UI. Same-app
      // hard nav so the resolver boots cleanly with the freshly stored JWT.
      if (isW2HostNow()) {
        window.location.href = '/select-plans';
        return;
      }
      // Raise the full-screen loader immediately so the onboarding welcome page
      // doesn't flash while we figure out where to send the user. It stays up
      // through the hard redirect below (and the destination keeps it until its
      // branding/config finishes loading).
      useGeneralStore.getState().setIsLoadingConfig(true);
      // If the user already has purchased plans, take them to their portal
      // (Health Hub) instead of the onboarding application.
      try {
        await useApplicationStore.getState().fetchActiveSubscriptionPlans();
      } catch {
        // ignore — fall back to the application on failure
      }
      const { activeSubscriptionPlans } = useApplicationStore.getState();
      window.location.href =
        activeSubscriptionPlans.length > 0 ? '/healthhub' : '/form';
      // navigate('/', { replace: true });
    }
  };

  return { clear, login: handleLogin, error, isLoading };
};
